import { useState } from 'react';
import type { ReactNode } from 'react';
import { useAction, useApi, useRead } from './context';
import { Confirm, Empty, Failure, Loading, Section, StatePill, When } from './bits';
import type { ApiFailure } from '../api/errors';
import type { JobKind } from '../wire';

/**
 * Conformance runs and import jobs — triggered here, executed elsewhere.
 *
 * Neither belongs *in* a request. A conformance run is `packages/contracts`
 * executing ~70 obligations against a base URL and an import is a LoveSpa
 * SQLite file becoming a tenant's year of history (spec §9): minutes of work, an
 * unbounded memory profile, and a failure mode that must not be "the operator's
 * browser tab closed". So each is a Cloud Run Job with its own service account
 * and its own timeout, and this screen holds the four verbs an operator needs —
 * run it, watch it, list what ran, read one execution.
 *
 * Two things this screen refuses to soften:
 *
 *  * **An import is a dry run unless it says otherwise.** Spec §9's gate is
 *    dry-run-by-default, and a console is exactly the surface where that default
 *    quietly inverts — one pre-ticked box and a year of somebody's history is
 *    written by accident. So the choice is a pair of radio buttons that start on
 *    dry run, executing is styled as the dangerous thing it is, and it takes a
 *    confirm that says what will be written.
 *  * **"Unconfigured" is a state, not an error.** `CONFORMANCE_JOB_NAME` and
 *    `IMPORT_JOB_NAME` are optional on purpose, so a console can be deployed
 *    before the jobs exist — and be the console you use to finish setting them
 *    up. When the server answers `503` naming the missing variable, this screen
 *    renders that sentence as a fact about the environment rather than as
 *    something that broke.
 *
 * The logs are not mirrored here and will not be. A job's output lives in Cloud
 * Logging under its own execution id; copying it through this service would put
 * an import's row-by-row output — which is tenant data — onto the one surface
 * that must never hold any (spec §6.3).
 */
export function Jobs({ tenantId }: { tenantId?: string | undefined }): ReactNode {
  const [job, setJob] = useState<JobKind>('conformance');
  // Bumped when a run starts, so the executions table re-reads once rather than
  // each panel keeping a second copy of the same list.
  const [ran, setRan] = useState(0);
  const started = (): void => setRan((n) => n + 1);

  return (
    <div className="view">
      <header className="view__head">
        <h2>Jobs</h2>
        <nav className="view__actions" aria-label="Job">
          <button
            type="button"
            className={`button${job === 'conformance' ? ' is-current' : ''}`}
            onClick={() => setJob('conformance')}
          >
            Conformance
          </button>
          <button
            type="button"
            className={`button${job === 'import' ? ' is-current' : ''}`}
            onClick={() => setJob('import')}
          >
            Import
          </button>
        </nav>
      </header>

      {job === 'conformance' ? (
        <ConformancePanel tenantId={tenantId} onStarted={started} ran={ran} />
      ) : (
        <ImportPanel tenantId={tenantId} onStarted={started} ran={ran} />
      )}
    </div>
  );
}

/** `503` naming a missing job variable is an environment fact, not a fault. */
function unconfiguredMessage(failure: ApiFailure | null): string | null {
  if (failure === null) return null;
  if (failure.code !== 'unavailable') return null;
  return /job is configured|is not configured/.test(failure.message) ? failure.message : null;
}

function Unconfigured({ message }: { message: string }): ReactNode {
  return (
    <div className="by-design" role="note">
      <h4>This job is not configured in this environment</h4>
      <p>{message}</p>
      <p className="by-design__rule">
        The variable is optional on purpose: a console deployed before its Cloud Run Jobs exist is
        still the console you would use to finish creating them. Nothing is broken — there is
        nothing to trigger yet.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Conformance                                                         */
/* ------------------------------------------------------------------ */

function ConformancePanel({
  tenantId,
  onStarted,
  ran,
}: {
  tenantId?: string | undefined;
  onStarted: () => void;
  ran: number;
}): ReactNode {
  const api = useApi();
  const action = useAction();
  const [tenant, setTenant] = useState(tenantId ?? '');
  const [baseUrl, setBaseUrl] = useState('');
  const [started, setStarted] = useState<string | null>(null);
  const unconfigured = unconfiguredMessage(action.failure);

  const run = async (): Promise<void> => {
    const ok = await action.run(async () => {
      const answer = await api.runJob('conformance', {
        tenant_id: tenant,
        ...(baseUrl === '' ? {} : { base_url: baseUrl }),
      });
      setStarted(
        `Started against ${answer.tenant_id}. ${answer.execution === null ? answer.note : `Execution ${answer.execution.execution_id}.`}`,
      );
    });
    if (ok) onStarted();
  };

  return (
    <>
      <Section
        title="Run the conformance suite"
        note="The ~70 obligations of the agreement, against a tenant's real book. The suite asserts against a deterministic fixture (spec §5.7.1) — a run against a tenant nobody seeded is a red result that means nothing."
      >
        {unconfigured !== null ? <Unconfigured message={unconfigured} /> : null}

        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            void run();
          }}
        >
          <div className="form__row">
            <TenantPicker value={tenant} onChange={setTenant} />
            <label className="field field--wide">
              <span className="field__label">Base URL override</span>
              <input
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                aria-label="Base URL override"
                placeholder="leave empty to use the job's configured PROVIDER_BASE_URL"
              />
            </label>
          </div>

          {unconfigured === null && action.failure !== null ? (
            <Failure failure={action.failure} />
          ) : null}
          {started !== null ? <p className="ok">{started}</p> : null}

          <div className="row-actions">
            <button
              type="submit"
              className="button button--primary"
              disabled={action.busy || tenant === ''}
            >
              {action.busy ? 'Starting…' : 'Run conformance'}
            </button>
          </div>
        </form>
      </Section>

      <Executions job="conformance" ran={ran} />
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Import                                                              */
/* ------------------------------------------------------------------ */

function ImportPanel({
  tenantId,
  onStarted,
  ran,
}: {
  tenantId?: string | undefined;
  onStarted: () => void;
  ran: number;
}): ReactNode {
  const api = useApi();
  const action = useAction();
  const [tenant, setTenant] = useState(tenantId ?? '');
  const [file, setFile] = useState('');
  const [execute, setExecute] = useState(false);
  const [asking, setAsking] = useState(false);
  const [started, setStarted] = useState<string | null>(null);
  const unconfigured = unconfiguredMessage(action.failure);

  const start = async (): Promise<void> => {
    const ok = await action.run(async () => {
      const answer = await api.runJob('import', {
        tenant_id: tenant,
        execute,
        ...(file === '' ? {} : { file }),
      });
      setStarted(
        `${answer.dry_run === true ? 'Dry run' : 'EXECUTING import'} started against ${answer.tenant_id}. ` +
          (answer.execution === null ? answer.note : `Execution ${answer.execution.execution_id}.`),
      );
    });
    if (ok) {
      setAsking(false);
      onStarted();
    }
  };

  return (
    <>
      <Section
        title="Import a LoveSpa database"
        note="Dry run first, always (spec §9). A dry run reads the SQLite file and reports what it would write; only an execute writes anything, and the to-the-cent reconciliation is what gates cutover."
      >
        {unconfigured !== null ? <Unconfigured message={unconfigured} /> : null}

        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            if (execute) setAsking(true);
            else void start();
          }}
        >
          <div className="form__row">
            <TenantPicker value={tenant} onChange={setTenant} />
            <label className="field field--wide">
              <span className="field__label">File path</span>
              <input
                value={file}
                onChange={(e) => setFile(e.target.value)}
                aria-label="File path"
                placeholder="gs://yourstore-imports/lovespa-2026-08-25.sqlite"
              />
              <span className="field__hint">
                Wherever the job can reach it. Nothing is uploaded through this console.
              </span>
            </label>
          </div>

          <fieldset className={`mode${execute ? ' mode--execute' : ''}`}>
            <legend>Mode</legend>
            <label className="mode__choice">
              <input
                type="radio"
                name="import-mode"
                checked={!execute}
                onChange={() => setExecute(false)}
              />
              <span>
                <strong>Dry run</strong> — read the file and report. Writes nothing. This is the
                default and it is the default on purpose.
              </span>
            </label>
            <label className="mode__choice">
              <input
                type="radio"
                name="import-mode"
                checked={execute}
                onChange={() => setExecute(true)}
              />
              <span>
                <strong>Execute</strong> — write the history into this tenant. Run a dry run first
                and read its reconciliation; this is not reversible from the console.
              </span>
            </label>
          </fieldset>

          {unconfigured === null && action.failure !== null ? (
            <Failure failure={action.failure} />
          ) : null}
          {started !== null ? <p className={execute ? 'warn' : 'ok'}>{started}</p> : null}

          <div className="row-actions">
            <button
              type="submit"
              className={`button ${execute ? 'button--danger' : 'button--primary'}`}
              disabled={action.busy || tenant === ''}
            >
              {action.busy ? 'Starting…' : execute ? 'Execute import' : 'Start dry run'}
            </button>
          </div>
        </form>
      </Section>

      <Executions job="import" ran={ran} />

      {asking ? (
        <Confirm
          title={`Execute the import into ${tenant}?`}
          consequence={
            'This writes a year of history into the tenant for real. Spec §9 gates cutover on a ' +
            'to-the-cent reconciliation of the dry run — if you have not read one for this file, ' +
            'cancel and run a dry run instead.'
          }
          confirmLabel="Execute the import"
          busy={action.busy}
          failure={action.failure}
          onConfirm={() => void start()}
          onCancel={() => {
            action.clear();
            setAsking(false);
          }}
        />
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Executions                                                          */
/* ------------------------------------------------------------------ */

function Executions({ job, ran }: { job: JobKind; ran: number }): ReactNode {
  const api = useApi();
  const list = useRead(() => api.executions(job), [job, ran]);
  const [open, setOpen] = useState<string | null>(null);
  const unconfigured = unconfiguredMessage(list.failure);

  return (
    <Section
      title="Recent executions"
      note="State is derived from the task counters, not read from a status field — an execution with no completion time and a name is running; one that completed with a failed task is failed."
      actions={
        <button type="button" className="button" onClick={list.reload} disabled={list.loading}>
          Refresh
        </button>
      }
    >
      {unconfigured !== null ? (
        <Unconfigured message={unconfigured} />
      ) : list.failure !== null ? (
        <Failure failure={list.failure} />
      ) : null}

      {list.value === null ? (
        list.loading ? (
          <Loading what="executions" />
        ) : null
      ) : list.value.executions.length === 0 ? (
        <Empty>Nothing has run yet.</Empty>
      ) : (
        <table className="table table--dense">
          <thead>
            <tr>
              <th>Execution</th>
              <th>State</th>
              <th>Started</th>
              <th>Completed</th>
              <th>Tasks</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.value.executions.map((e) => (
              <tr key={e.execution_id}>
                <td>
                  <code>{e.execution_id}</code>
                </td>
                <td>
                  <StatePill state={e.state} />
                </td>
                <td>
                  <When iso={e.started_at} />
                </td>
                <td>
                  <When iso={e.completed_at} empty="running" />
                </td>
                <td>
                  {e.succeeded_count ?? 0}/{e.task_count ?? 0}
                  {(e.failed_count ?? 0) > 0 ? (
                    <span className="flag flag--bad">{e.failed_count} failed</span>
                  ) : null}
                </td>
                <td>
                  <button
                    type="button"
                    className="linkish"
                    onClick={() => setOpen(open === e.execution_id ? null : e.execution_id)}
                  >
                    {open === e.execution_id ? 'Hide' : 'Detail'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {open !== null ? <ExecutionDetailPanel job={job} executionId={open} /> : null}
    </Section>
  );
}

function ExecutionDetailPanel({
  job,
  executionId,
}: {
  job: JobKind;
  executionId: string;
}): ReactNode {
  const api = useApi();
  const detail = useRead(() => api.execution(job, executionId), [job, executionId]);

  return (
    <div className="panel">
      {detail.failure !== null ? <Failure failure={detail.failure} /> : null}
      {detail.value === null ? (
        detail.loading ? (
          <Loading what="the execution" />
        ) : null
      ) : (
        <>
          <dl className="facts">
            <div>
              <dt>Execution</dt>
              <dd>
                <code>{detail.value.execution.execution_id}</code>
              </dd>
            </div>
            <div>
              <dt>Resource</dt>
              <dd className="wrap muted">{detail.value.execution.name}</dd>
            </div>
            <div>
              <dt>State</dt>
              <dd>
                <StatePill state={detail.value.execution.state} />
              </dd>
            </div>
            <div>
              <dt>Created</dt>
              <dd>
                <When iso={detail.value.execution.created_at} />
              </dd>
            </div>
            <div>
              <dt>Started</dt>
              <dd>
                <When iso={detail.value.execution.started_at} />
              </dd>
            </div>
            <div>
              <dt>Completed</dt>
              <dd>
                <When iso={detail.value.execution.completed_at} empty="running" />
              </dd>
            </div>
          </dl>
          <p className="muted">
            The output is not here. A job’s logs live in Cloud Logging under this execution id,
            where the job’s own service account wrote them — an import’s row-by-row output is tenant
            data, and copying it through this service would put it on the one surface that must
            never hold any (spec §6.3).
          </p>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The tenant picker                                                   */
/* ------------------------------------------------------------------ */

/**
 * A picker rather than a text box, because `admin-api` checks the tenant exists
 * before it starts a job and a typo is fifteen minutes of red that means
 * nothing. The list is the fleet read, which the operator is entitled to.
 */
export function TenantPicker({
  value,
  onChange,
  label = 'Tenant',
}: {
  value: string;
  onChange: (tenantId: string) => void;
  label?: string;
}): ReactNode {
  const api = useApi();
  const fleet = useRead(() => api.fleet({ limit: 200 }), []);

  return (
    <label className="field">
      <span className="field__label">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
        <option value="">choose a tenant…</option>
        {(fleet.value?.tenants ?? []).map((t) => (
          <option key={t.tenant_id} value={t.tenant_id}>
            {t.tenant_id} — {t.name} ({t.status})
          </option>
        ))}
      </select>
      {fleet.failure !== null ? (
        <span className="field__hint">Could not list tenants: {fleet.failure.message}</span>
      ) : null}
    </label>
  );
}
